import { ArrowLeftIcon, LinkIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatDateStr } from '../../../../common-utils/formatters';
import type { RootState } from '../../../store/store';
import { getInsuranceExclusions, getNafCodesHierarchy, getNafExclusions } from '../../../utils/api';
import { BauxDict } from '../../../utils/dictionnayMap';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';
import WarningValidationPending from '../../common/WarningValidationPending';

interface NafExclusion {
  id: number;
  nafCode: string;
  nafLevel: string;
}

const headers = [
  { title: 'SIREN' },
  { title: 'Nom SIREN' },
  { title: 'Code NAF' },
  { title: 'Adresse' },
  { title: 'Nom de la copro' },
  { title: 'Gestionnaire' },
  { title: 'Date potentielle de période triennale' },
  { title: 'Voir le détail', class: 'min-w-[120px]' },
];

const sortableColumns = [
  'SIREN', 'Nom SIREN', 'Code NAF', 'Adresse', 'Nom de la copro', 'Gestionnaire', 'Date potentielle de période triennale',
];

interface NafNode {
  code: string;
  level: string;
  label: string;
  children?: Record<string, NafNode>;
}

function InsuranceExclusions() {
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  interface InsuranceExclusionCompany {
    siren?: string;
    denominationUniteLegale?: string;
    sexeUniteLegale?: string;
    prenomUsuelUniteLegale?: string;
    nomUniteLegale?: string;
    activitePrincipaleEtablissement?: string;
    numeroVoieEtablissement?: string;
    typeVoieEtablissement?: string;
    libelleVoieEtablissement?: string;
    codePostalEtablissement?: string;
    libelleCommuneEtablissement?: string;
    idCopro?: number;
    nameCopro?: string;
    gestionnaireCopro?: { firstname?: string; lastname?: string; email?: string };
    leaseEndDate?: string;
    [key: string]: unknown;
  }
  const [companies, setCompanies] = useState<InsuranceExclusionCompany[] | null>(null);
  const [exclusions, setExclusions] = useState<NafExclusion[]>([]);
  const [nafLabelsMap, setNafLabelsMap] = useState<Record<string, string>>({});
  const [selectedNafFilter, setSelectedNafFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRowDetails, setSelectedRowDetails] = useState<number | null>(null);

  // Build a flat map of code -> label from the hierarchy
  const buildLabelsMap = useCallback((hierarchy: Record<string, NafNode>): Record<string, string> => {
    const map: Record<string, string> = {};
    
    const traverse = (node: NafNode) => {
      map[node.code] = node.label;
      if (node.children) {
        Object.values(node.children).forEach(traverse);
      }
    };
    
    Object.values(hierarchy).forEach(traverse);
    return map;
  }, []);

  // Get label for a NAF code from the map
  const getNafLabel = useCallback((code: string): string => {
    return nafLabelsMap[code] || code;
  }, [nafLabelsMap]);

  const handleGoBack = () => {
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch (error) {
      console.error('Navigation error:', error);
      navigate(ROUTES.HOME);
    }
  };

  const fetchExclusions = useCallback(async() => {
    if (!company?.id || !jwt) {return;}

    try {
      // Fetch exclusions and NAF hierarchy in parallel
      const [exclusionsResponse, hierarchyResponse] = await Promise.all([
        fetch(getNafExclusions(company.id), JWTHeader(jwt)),
        fetch(getNafCodesHierarchy(), JWTHeader(jwt)),
      ]);

      if (exclusionsResponse.ok) {
        const data = await exclusionsResponse.json();
        setExclusions(data);
      }

      if (hierarchyResponse.ok) {
        const hierarchyData = await hierarchyResponse.json();
        setNafLabelsMap(buildLabelsMap(hierarchyData));
      }
    } catch (error) {
      console.error('Error fetching exclusions:', error);
    }
  }, [company?.id, jwt, buildLabelsMap]);

  const fetchCompanies = useCallback(async() => {
    if (!company?.id || !jwt) {return;}

    setIsLoading(true);
    try {
      const url = getInsuranceExclusions(company.id, selectedNafFilter || undefined);
      const response = await fetch(url, JWTHeader(jwt));

      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }

      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      showToast('error', 'Une erreur est survenue lors du chargement des entreprises.');
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  }, [company?.id, jwt, selectedNafFilter]);

  useEffect(() => {
    fetchExclusions();
  }, [fetchExclusions]);

  useEffect(() => {
    if (exclusions.length > 0) {
      fetchCompanies();
    } else {
      setCompanies([]);
      setIsLoading(false);
    }
  }, [exclusions, fetchCompanies]);

  const handleDetails = (index: number) => {
    setIsPopupOpen(true);
    setSelectedRowDetails(index);
  };

  const renderNestedObject = (obj: Record<string, unknown>, parentKey = '') => (
    <MUITable
      data={Object.keys(obj).map((key) => {
        const value = obj[key];
        const displayKey = parentKey ? `${parentKey}.${key}` : key;
        const displayLabel = BauxDict[key] ? BauxDict[key] : key;
                if (typeof value === 'object' && value !== null) {
          const objValue = value as Record<string, unknown>;
          return {
            Libellé: <strong>{displayLabel}</strong>,
            Valeur: (
              <div key={displayKey}>
                {key === 'gestionnaireCopro'
                  ? (objValue.email ? `${String(objValue.firstname ?? '')} ${String(objValue.lastname ?? '').toUpperCase()} - ${String(objValue.email)}` : 'N/A')
                  : renderNestedObject(objValue, displayKey)}
              </div>
            ),
          };
        }
        return {
          Libellé: <strong>{displayLabel}</strong>,
          Valeur: (
            <div key={displayKey}>{String(value)}</div>
          ),
        };
      })}
      headers={[
        { title: 'Libellé', class: 'w-1/2', customContentClass: 'w-1/2' },
        { title: 'Valeur', class: 'w-1/2', customContentClass: 'w-1/2 break-words break-all whitespace-normal' },
      ]}
      isSearchable={false}
      disablePagination
      tableClass="table-fixed"
    />
  );

  const renderPopupTitle = (selectedIndex: number | null) => {
    if (selectedIndex === null || !companies || !companies[selectedIndex]) {return "Détail de l'entreprise";}

    const siren = companies[selectedIndex].siren;

    return (
      <div className="items-center md:flex md:justify-center">
        <span>Détail de l'entreprise</span>
        {siren && (
          <a
            href={`https://www.pappers.fr/entreprise/${siren}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 ml-0 flex w-full items-center rounded bg-rgpd-blue-dark px-3 py-2 text-sm text-white shadow-md md:mt-0 md:ml-4 md:w-auto"
          >
            <LinkIcon className="mr-1 h-4 w-4" />
            Ouvrir dans Pappers
          </a>
        )}
      </div>
    );
  };

  const getLevelLabel = (level: string): string => {
    switch (level) {
      case 'division':
        return 'Division';
      case 'group':
        return 'Groupe';
      case 'class':
        return 'Classe';
      case 'subclass':
        return 'Sous-classe';
      default:
        return level;
    }
  };

  return (
    <div className="rounded-lg bg-white p-8 shadow-md">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
      </div>

      <div className="leading-relaxed text-slate-800">
        {/* Title Banner */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-orange-500 px-8 py-5 text-center text-white">
          <div className="relative z-10">
            <div className="mb-2 flex items-center justify-center gap-3">
              <ShieldExclamationIcon className="h-8 w-8" />
              <h1 className="text-xl font-bold tracking-tight">
                Surveillance exclusion assurance (code NAF)
              </h1>
            </div>
            <div className="mx-auto max-w-2xl text-base font-normal opacity-90">
              Entreprises présentes dans vos copropriétés avec des codes NAF exclus par les assurances
            </div>
          </div>
        </div>

        <div className="mb-8 text-base">
          <WarningValidationPending />

          {/* Exclusions summary */}
          {exclusions.length > 0 ? (
            <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="mb-3 flex items-center justify-start">
                <h3 className="font-semibold text-orange-800">
                  Codes NAF exclus configurés ({exclusions.length})
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {exclusions.map((exclusion) => (
                  <span
                    key={exclusion.nafCode}
                    className="rounded bg-orange-100 px-3 py-1.5 text-sm text-orange-800"
                    title={getNafLabel(exclusion.nafCode)}
                  >
                    <span className="font-mono font-semibold">{exclusion.nafCode}</span>
                    <span className="ml-1 text-xs">({getLevelLabel(exclusion.nafLevel)})</span>
                    <span className="ml-1 text-xs text-orange-600">- {getNafLabel(exclusion.nafCode)}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="mb-3 text-yellow-800">
                Aucun code NAF n'est configuré pour l'exclusion assurance.
              </p>
            </div>
          )}

          {/* NAF Filter */}
          {exclusions.length > 0 && (
            <div className="mb-6 flex items-center gap-4">
              <label className="font-medium text-gray-700">Filtrer par code NAF :</label>
              <select
                value={selectedNafFilter}
                onChange={(e) => setSelectedNafFilter(e.target.value)}
                className="min-w-[350px] rounded-lg border border-gray-300 px-4 py-2 focus:border-rgpd-blue focus:ring-2 focus:ring-rgpd-blue"
              >
                <option value="">Tous les codes exclus</option>
                {exclusions.map((exclusion) => (
                  <option key={exclusion.nafCode} value={exclusion.nafCode}>
                    {exclusion.nafCode} ({getLevelLabel(exclusion.nafLevel)}) - {getNafLabel(exclusion.nafCode)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <div className="w-full overflow-x-scroll">
            {isLoading ? (
              <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
            ) : companies && companies.length > 0 ? (
              <MUITable
                data={companies.map((companyEntry: InsuranceExclusionCompany, index: number) => {
                  const address = [
                    companyEntry.numeroVoieEtablissement,
                    companyEntry.typeVoieEtablissement,
                    companyEntry.libelleVoieEtablissement,
                    companyEntry.codePostalEtablissement,
                    companyEntry.libelleCommuneEtablissement,
                  ].join(' ');

                  return {
                    SIREN: companyEntry.siren,
                    'Nom SIREN': companyEntry.denominationUniteLegale
                      || ([
                        companyEntry.sexeUniteLegale === 'M' ? 'MONSIEUR' : 'MADAME',
                        companyEntry.prenomUsuelUniteLegale,
                        companyEntry.nomUniteLegale,
                      ].join(' ')),
                    'Code NAF': (
                      <span className="rounded bg-orange-100 px-2 py-1 font-mono text-sm text-orange-800">
                        {companyEntry.activitePrincipaleEtablissement || 'N/A'}
                      </span>
                    ),
                    Adresse: (
                      <div className="flex items-center">
                        <div>
                          <RedirectionMapButtons address={address} />
                        </div>
                        <span>{address}</span>
                      </div>
                    ),
                    'Nom de la copro': (
                      <div className="flex items-center">
                        <RedirectionCoproButtons coproId={companyEntry.idCopro} />
                        {companyEntry.nameCopro}
                      </div>
                    ),
                    Gestionnaire: companyEntry.gestionnaireCopro?.firstname
                      ? `${companyEntry.gestionnaireCopro.firstname} ${
                        companyEntry.gestionnaireCopro.lastname ? companyEntry.gestionnaireCopro.lastname.toUpperCase() : ''}`
                      : '',
                    'Date potentielle de période triennale': formatDateStr(companyEntry.leaseEndDate),
                    'Voir le détail': (
                      <div className="flex justify-center">
                        <button
                          className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto"
                          onClick={() => handleDetails(index)}
                        >
                          Voir le détail
                        </button>
                      </div>
                    ),
                  };
                })}
                headers={headers}
                tableClass="bg-rgpd-blue-light"
                sortableColumns={sortableColumns}
                isSearchable
                filterColumnName="Code NAF"
              />
            ) : exclusions.length > 0 ? (
              <div className="flex w-full place-content-center">
                <Badge
                  mode="info"
                  text="Aucune entreprise trouvée avec les codes NAF exclus dans vos copropriétés"
                  className="my-10"
                />
              </div>
            ) : null}
          </div>

          {isPopupOpen && (
            <Popup
              isOpen
              title={renderPopupTitle(selectedRowDetails)}
              onClose={() => {
                setIsPopupOpen(false);
                setSelectedRowDetails(null);
              }}
            >
              {selectedRowDetails !== null && companies && (
                <div className="max-h-[75dvh] gap-2 overflow-y-scroll">
                  {renderNestedObject(companies[selectedRowDetails])}
                </div>
              )}
              {!companies && (
                <Skeleton count={10} height={25} width={400} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
              )}
            </Popup>
          )}
        </div>
      </div>
    </div>
  );
}

export default InsuranceExclusions;
